import { computed, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import { useTrayViewStore } from '@/js/core/stores/tray-view-store';
import { useLoadingStore } from '@/js/core/stores/loading-store';
import { useTraysListStore } from '@/js/core/stores/trays-list-store';
import { getTray, updateTray as apiUpdateTray } from '@/js/core/utils/trays-api';
import { useNotFoundRedirect } from '@/js/core/composables/useNotFoundRedirect';
import { matchesTrayRouteSlug, trayRouteParams, trayRouteSlug } from '@/js/core/utils/tray-route';

export function useTrayView({ traySlug, trayUuid, startPolling }) {
  const trayViewStore = useTrayViewStore();
  const loadingStore = useLoadingStore();
  const traysListStore = useTraysListStore();
  const { redirectOnNotFound } = useNotFoundRedirect();
  const router = useRouter();

  const tray = computed(() => trayViewStore.tray);
  const comments = computed(() => trayViewStore.comments);
  const error = computed(() => trayViewStore.error);
  const isReady = computed(() => tray.value !== null || error.value !== '');

  const trayDetailRef = ref(null);

  const handleUpdateTray = async payload => {
    const uuid = trayUuid.value;
    if (!uuid) {
      trayDetailRef.value?.onSaveError('No tray ID provided');
      return;
    }

    try {
      await apiUpdateTray(uuid, payload);
    } catch (err) {
      trayDetailRef.value?.onSaveError(err.message || 'Failed to update tray');
      return;
    }

    // Commits the queued manager changes, so it has to finish before the re-read below.
    await trayDetailRef.value?.onSaveComplete();

    try {
      // Re-read the tray so server-computed fields (e.g. the members roster) reflect the
      // save — the update response can omit the freshly-synced roster.
      const response = await getTray(uuid);
      const updatedTray = response.data;
      trayViewStore.updateTray(updatedTray);
      traysListStore.invalidate();

      if (trayRouteSlug(updatedTray) && !matchesTrayRouteSlug(updatedTray, traySlug.value)) {
        await router.replace({ name: 'tray-view', params: trayRouteParams(updatedTray) });
      }
    } catch (err) {
      // The writes already landed; only the local view is stale, so this is not a save failure.
      console.error('Failed to refresh tray after save', err);
    }
  };

  const fetchFromApi = async uuid => {
    loadingStore.start();
    try {
      await trayViewStore.ensureTray(uuid);

      if (!matchesTrayRouteSlug(trayViewStore.tray, traySlug.value)) {
        trayViewStore.setError('Tray not found', 'not_found');
        return false;
      }

      return true;
    } catch (err) {
      if (redirectOnNotFound(err)) return false;

      trayViewStore.setError(err.message || 'Failed to load tray', err.code || '');
      return false;
    } finally {
      loadingStore.complete();
    }
  };

  const loadTrayData = async uuid => {
    if (!uuid) {
      trayViewStore.setError('No tray ID provided');
      return;
    }

    if (trayViewStore.hasDataForUuid(uuid)) {
      if (tray.value && !matchesTrayRouteSlug(tray.value, traySlug.value)) {
        trayViewStore.setError('Tray not found', 'not_found');
        return;
      }

      if (!error.value) {
        startPolling();
      }

      return;
    }

    // Client-side navigation or no matching hydrated data: fetch from API
    if (await fetchFromApi(uuid)) {
      startPolling();
    }
  };

  // Handle SPA navigation between different trays
  watch(
    traySlug,
    async () => {
      if (trayUuid.value) {
        await loadTrayData(trayUuid.value);
        return;
      }

      trayViewStore.setError('No tray ID provided', 'not_found');
    },
    { immediate: true }
  );

  return { tray, comments, error, isReady, trayDetailRef, handleUpdateTray };
}
